import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type RadialBlurMode = "zoom" | "spin";

export interface RadialBlurParams {
  amount: number;
  mode: RadialBlurMode;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const RadialBlur: Filter<RadialBlurParams> = {
  id: "radial-blur",
  displayName: "Radial Blur",
  defaultParams: { amount: 10, mode: "zoom" },

  apply(input: PixelBuffer, params: RadialBlurParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0 || params.amount < 1) {
      dst.set(src);
      return out;
    }

    const cx = w / 2, cy = h / 2;
    const amount = Math.min(params.amount, 100);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0, count = 0;

        if (params.mode === "zoom") {
          const dx = x - cx, dy = y - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxDist = Math.sqrt(cx * cx + cy * cy) || 1;
          const steps = Math.max(1, Math.round(amount * (dist / maxDist)));
          for (let s = 0; s < steps; s++) {
            const t = 1 - s / steps;
            const sx = Math.round(cx + dx * t);
            const sy = Math.round(cy + dy * t);
            if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
              const si = (sy * w + sx) * 4;
              sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!; sumA += src[si + 3]!;
              count++;
            }
          }
        } else {
          const dx = x - cx, dy = y - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxAngle = (amount / 100) * Math.PI * 2;
          const steps = Math.max(1, Math.round(amount * (dist / (Math.max(cx, cy) || 1))));
          for (let s = 0; s < steps; s++) {
            const angle = (s / steps) * maxAngle;
            const cosA = Math.cos(angle), sinA = Math.sin(angle);
            const sx = Math.round(cx + dx * cosA - dy * sinA);
            const sy = Math.round(cy + dx * sinA + dy * cosA);
            if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
              const si = (sy * w + sx) * 4;
              sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!; sumA += src[si + 3]!;
              count++;
            }
          }
        }

        const o = (y * w + x) * 4;
        if (count > 0) {
          dst[o] = clamp8(Math.round(sumR / count));
          dst[o + 1] = clamp8(Math.round(sumG / count));
          dst[o + 2] = clamp8(Math.round(sumB / count));
          dst[o + 3] = clamp8(Math.round(sumA / count));
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
